import { ToolBase, ToolPointer, ShiftLock } from './tool';
import { SnapResult } from '../inference/inference';
import { AXIS_COLORS, SNAP_COLORS } from '../render/preview';
import { add, dist, mul, normalize, sub, eq, Vec3, AXIS_Z } from '../core/vec';
import { addLineSegment } from '../core/lines';
import { formatLength, parseLength } from '../core/units';

/**
 * Linien-Werkzeug (L): Kette von Kanten mit SketchUp-Inferenz.
 * Klicken zeichnet verbundene Segmente; Klick auf den Startpunkt schließt die Fläche.
 * Esc beendet die Kette. Pfeiltasten sperren Achsen. Zahlen = exakte Länge (VCB).
 */
export class LineTool extends ToolBase {
  readonly id = 'line';
  readonly label = 'Linie';
  readonly shortcut = 'L';
  readonly cursor = 'crosshair';
  readonly hint = 'Länge auswählen · Pfeiltasten = Achse sperren · Zahl tippen = exakte Länge · Esc = Kette beenden';

  private state: 'idle' | 'drawing' = 'idle';
  private chain: Vec3[] = [];
  private plane: { point: Vec3; normal: Vec3 } | null = null;
  private planeFaceId: number | null = null;
  private lockedAxis: 0 | 1 | 2 | null = null;
  /** Umschalt gehalten → zuletzt eingerastete Achse bleibt gesperrt. */
  private shiftLock = new ShiftLock<0 | 1 | 2>();
  private lastAxis: 0 | 1 | 2 | null = null;
  private snap: SnapResult | null = null;
  private lockedPoint: Vec3 | null = null;
  private currentEnd: Vec3 | null = null;

  enter(): void {
    this.reset();
  }

  exit(): void {
    this.reset();
  }

  private reset(): void {
    this.state = 'idle';
    this.chain = [];
    this.plane = null;
    this.planeFaceId = null;
    this.lockedAxis = null;
    this.shiftLock.reset();
    this.lastAxis = null;
    this.lockedPoint = null;
    this.currentEnd = null;
    this.ctx.preview.clear();
    this.ctx.requestUpdate();
  }

  pointerMove(e: ToolPointer): void {
    this.ctx.preview.clear();
    // Ohne Flächenebene: freie Punkte auf der Höhe des letzten Punkts (nicht am Boden)
    const last = this.state === 'drawing' ? this.chain[this.chain.length - 1] : undefined;
    const fallback = this.plane ?? (last ? { point: last, normal: AXIS_Z } : null);
    this.snap = this.ctx.inference.snap(e.x, e.y, e.ray, { fallbackPlane: fallback });
    this.showSnapDot(this.snap);

    if (this.state !== 'drawing' || this.chain.length === 0) {
      this.currentEnd = this.snap.point;
      this.ctx.requestUpdate();
      return;
    }
    const start = this.chain[this.chain.length - 1]!;
    const lock = this.lockedAxis ?? this.shiftLock.update(e.shift, () => this.lastAxis);
    // Reihenfolge wie SketchUp: gesperrte Achse > Eck-/Kanten-Fang > Achs-Inferenz > freier Punkt
    let point = this.snap.point;
    let axis: 0 | 1 | 2 | null = null;
    const discrete = ['endpoint', 'midpoint', 'onEdge', 'onGuide', 'origin'].includes(this.snap.kind);
    if (lock !== null && discrete) {
      // Gesperrte Achse + Fangpunkt: Punkt auf die Achse projizieren (z. B. bis Höhe einer Ecke)
      const a = [{ x: 1, y: 0, z: 0 }, { x: 0, y: 1, z: 0 }, AXIS_Z][lock]!;
      const t = (this.snap.point.x - start.x) * a.x + (this.snap.point.y - start.y) * a.y + (this.snap.point.z - start.z) * a.z;
      point = add(start, mul(a, t));
      axis = lock;
    } else if (lock !== null || !discrete) {
      const inf = this.ctx.inference.axisInference(start, e.x, e.y, e.ray, lock);
      if (inf) {
        point = inf.point;
        axis = inf.axis;
      }
    }
    this.lastAxis = axis;
    this.lockedPoint = axis !== null ? point : null;
    this.currentEnd = point;

    // Vorschaulinie: Achsenfarbe bei Sperre/Parallelen, sonst dunkel
    const color = axis !== null ? AXIS_COLORS[axis] : 0x333333;
    this.ctx.preview.line(start, point, color);
    this.ctx.preview.dot(point, axis !== null ? AXIS_COLORS[axis] : SNAP_COLORS[this.snap.kind] ?? 0x333333, 7);

    // Live-Maß in der Statusleiste
    this.ctx.ui.setStatus(
      `Länge: ${formatLength(dist(start, point), this.ctx.units())}` +
        (axis !== null ? ` · ${['rote', 'grüne', 'blaue'][axis]} Achse` : '') +
        (lock !== null ? (this.lockedAxis !== null ? ' gesperrt' : ' gesperrt (Umschalt)') : '') +
        ' · Zahl tippen für exakte Länge'
    );
    this.ctx.requestUpdate();
  }

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    this.pointerMove(e); // Frischen Snap für die aktuelle Position holen
    const snap = this.snap!;
    const point = this.currentEnd ?? snap.point;

    if (this.state === 'idle') {
      this.chain = [point];
      this.state = 'drawing';
      this.lockedAxis = null;
      if (snap.plane && snap.faceId !== undefined) {
        // Nur echte Flächen bestimmen die Zeichenebene; Boden/Raster nicht (sonst fallen
        // freie Punkte nach einer senkrechten Linie wieder auf z = 0)
        this.plane = snap.plane;
        this.planeFaceId = snap.faceId;
      } else {
        // Freier Start: Folgepunkte bleiben auf der Höhe des jeweils letzten Punkts
        this.plane = null;
        this.planeFaceId = null;
      }
      this.ctx.ui.setStatus('Endpunkt auswählen');
      return;
    }

    // Zeichnen: Kette fortsetzen
    const start = this.chain[this.chain.length - 1]!;
    if (dist(start, point) < 1e-6) return;

    // Jede Linie teilt getroffene Kanten; schließt sie einen ebenen Ring, entsteht
    // automatisch eine Fläche (quer über eine Fläche → Fläche wird geteilt), wie SketchUp
    this.ctx.beginEdit();
    const res = addLineSegment(this.ctx.model, start, point);
    this.ctx.requestUpdate();

    // Auf Kettenanfang geschlossen oder Fläche entstanden → Linienzug beenden
    if ((this.chain.length >= 2 && eq(point, this.chain[0]!, 1e-4)) || res.faceIds.length > 0) {
      this.reset();
      this.ctx.ui.setStatus(null);
      return;
    }
    this.chain.push(point);
    // Achssperre gilt pro Segment (wie SketchUp)
    this.lockedAxis = null;
    this.shiftLock.reset();
  }

  pointerUp(_e: ToolPointer): void {}

  keyDown(e: KeyboardEvent): void {
    if (e.key === 'ArrowLeft') this.toggleLock(1);
    else if (e.key === 'ArrowRight') this.toggleLock(0);
    else if (e.key === 'ArrowUp') this.toggleLock(2);
    else if (e.key === 'ArrowDown') this.lockedAxis = null;
  }

  private toggleLock(axis: 0 | 1 | 2): void {
    this.lockedAxis = this.lockedAxis === axis ? null : axis;
    if (this.state === 'drawing' && this.currentEnd && this.chain.length > 0) {
      const start = this.chain[this.chain.length - 1]!;
      this.currentEnd = this.ctx.inference.axisLock(start, this.currentEnd, this.lockedAxis).point;
      this.pointerMoveFromCurrent();
    }
  }

  private pointerMoveFromCurrent(): void {
    // Vorschau nach Achsenwechsel aktualisieren wird beim nächsten pointerMove ohnehin gemacht
    this.ctx.requestUpdate();
  }

  wantsVcb(): boolean {
    return this.state === 'drawing';
  }

  vcbDisplay(): string | null {
    if (this.state !== 'drawing' || !this.currentEnd || this.chain.length === 0) return null;
    return formatLength(dist(this.chain[this.chain.length - 1]!, this.currentEnd), this.ctx.units());
  }

  applyVcb(raw: string): void {
    if (this.state !== 'drawing' || this.chain.length === 0) return;
    const parsed = parseLength(raw, this.ctx.units());
    if (parsed === null || !Number.isFinite(parsed) || Math.abs(parsed) < 1e-9) return;
    const start = this.chain[this.chain.length - 1]!;
    let dir: Vec3 | null = null;
    if (this.lockedAxis !== null) {
      const axisDir = [{ x: 1, y: 0, z: 0 }, { x: 0, y: 1, z: 0 }, { x: 0, y: 0, z: 1 }][this.lockedAxis]!;
      // Vorzeichen aus der aktuellen Cursorlage
      let sign = 1;
      if (this.currentEnd) {
        const d = sub(this.currentEnd, start);
        const comp = this.lockedAxis === 0 ? d.x : this.lockedAxis === 1 ? d.y : d.z;
        sign = comp < 0 ? -1 : 1;
      }
      dir = mul(axisDir, sign);
    } else if (this.currentEnd && dist(start, this.currentEnd) > 1e-6) {
      dir = normalize(sub(this.currentEnd, start));
    }
    if (!dir) return;
    const end = add(start, mul(dir, Math.abs(parsed)));
    this.ctx.beginEdit();
    const res = addLineSegment(this.ctx.model, start, end);
    if (res.faceIds.length > 0 || (this.chain.length >= 2 && eq(end, this.chain[0]!, 1e-4))) {
      this.reset();
      this.ctx.requestUpdate();
      return;
    }
    this.chain.push(end);
    this.lockedPoint = null;
    this.ctx.requestUpdate();
  }

  cancel(): void {
    // Esc: Kette beenden, Werkzeug bleibt aktiv
    this.reset();
    this.ctx.ui.setStatus(null);
  }

  private showSnapDot(snap: SnapResult): void {
    if (snap.kind !== 'ground') {
      this.ctx.preview.dot(snap.point, SNAP_COLORS[snap.kind] ?? 0x333333, snap.priority >= 4 ? 11 : 8);
    }
  }
}
